export const comprehensiveReactCourse = {
  id: 'react-mastery-complete-2024',
  title: 'React Mastery: IntermediateDevelopment Course',
  description: 'Master React from fundamentals to advanced patterns with 8+ hours of hands-on learning including projects, quizzes, and real-world applications.',
  shortDescription: 'Complete 8+ hour React course with hands-on projects, quizzes, and real-world applications.',
  category: 'Web Development',
  industryCategory: 'Technology',
  difficulty: 'intermediate' as const,
  duration: 8.5, // hours
  estimatedWeeks: 6,
  price: 0,
  instructorId: 'platform',
  instructorName: 'Workazon Platform',
  instructorBio: 'Expert-curated content from industry professionals',
  status: 'published' as const,
  tags: ['React', 'JavaScript', 'Frontend', 'Web Development', 'Hooks', 'JSX'],
  skills: [
    'React Components & JSX',
    'State Management (useState, useReducer, Context)',
    'React Hooks (useEffect, useMemo, useCallback)',
    'React Router',
    'API Integration',
    'Testing React Apps',
    'Performance Optimization',
    'Modern React Patterns'
  ],
  prerequisites: ['JavaScript ES6+', 'HTML/CSS', 'Git Basics'],
  skillsLearned: [
    'React Components & JSX',
    'State Management (useState, useReducer, Context)',
    'React Hooks (useEffect, useMemo, useCallback)',
    'React Router',
    'API Integration',
    'Testing React Apps',
    'Performance Optimization',
    'Modern React Patterns'
  ],
  imageUrl: '/images/courses/react.jpg',
  learningObjectives: [
    'Build dynamic React applications from scratch',
    'Master React hooks and state management',
    'Implement routing and navigation',
    'Connect to APIs and handle data',
    'Write tests for React components',
    'Optimize React app performance',
    'Deploy React applications',
    'Apply modern React patterns and best practices'
  ],
  marketData: {
    averageSalary: 95000,
    careerPaths: ['React Developer', 'Frontend Engineer', 'Full Stack Developer'],
    jobDemand: 3200,
    marketTrend: 'rising',
    relatedSkills: ['JavaScript', 'TypeScript', 'Node.js', 'GraphQL'],
    salaryRange: {
      min: 70000,
      max: 140000
    }
  },
  
  // Course structure
  template: 'comprehensive',
  quizzes: [], // Individual quizzes are in modules
  assignments: [],
  
  // Statistics  
  enrollmentCount: 1847,
  completionRate: 78,
  averageRating: 4.9,
  reviewCount: 452,
  
  // Legacy fields
  createdBy: 'platform',
  isPublic: true,
  
  // Metadata
  createdAt: new Date('2024-01-09T19:00:00Z'),
  updatedAt: new Date('2024-01-09T19:00:00Z'), 
  lastModified: new Date('2024-01-09T19:00:00Z'),
  thumbnailUrl: 'https://images.unsplash.com/photo-1633356122544-f134324a6cee?w=400',
  modules: [
    {
      id: 'react-fundamentals',
      title: 'React Fundamentals',
      description: 'Introduction to React concepts, JSX, and your first components',
      order: 1,
      estimatedHours: 1.5,
      content: [
        {
          id: 'what-is-react',
          title: 'What is React?',
          type: 'video',
          duration: 15,
          description: 'Understanding React, its benefits, and the ecosystem'
        },
        {
          id: 'setup-environment',
          title: 'Setting Up Development Environment',
          type: 'text',
          duration: 20,
          description: 'Install Node.js, create-react-app, and essential tools',
          textContent: `# Setting Up Your React Development Environment

## Prerequisites
Before we start building React applications, we need to set up our development environment. This lesson will guide you through installing the necessary tools and creating your first React project.

## Required Software

### 1. Node.js and npm
React applications require Node.js to run the development server and build tools.

**Installation Steps:**
1. Visit [nodejs.org](https://nodejs.org)
2. Download the LTS (Long Term Support) version
3. Run the installer and follow the setup wizard
4. Verify installation by opening terminal/command prompt and running:
   \`\`\`bash
   node --version
   npm --version
   \`\`\`

### 2. Code Editor
We recommend Visual Studio Code with these essential extensions:
- ES7+ React/Redux/React-Native snippets
- Prettier - Code formatter
- Auto Rename Tag
- Bracket Pair Colorizer

## Creating Your First React App

### Using Create React App
Create React App is the official tool for bootstrapping React projects with zero configuration.

\`\`\`bash
npx create-react-app my-first-react-app
cd my-first-react-app
npm start
\`\`\`

This command will:
- Create a new directory with all necessary files
- Install all dependencies
- Set up the development server
- Open your app in the browser at http://localhost:3000

## Project Structure Overview

\`\`\`
my-first-react-app/
├── public/
│   ├── index.html
│   └── favicon.ico
├── src/
│   ├── App.js
│   ├── App.css
│   ├── index.js
│   └── index.css
├── package.json
└── README.md
\`\`\`

### Key Files:
- **public/index.html**: The HTML template
- **src/index.js**: Entry point of your React application
- **src/App.js**: Main component of your application
- **package.json**: Project dependencies and scripts

## Development Workflow

### Available Scripts:
- \`npm start\`: Runs the development server
- \`npm test\`: Runs the test suite
- \`npm run build\`: Builds the app for production
- \`npm run eject\`: Ejects from Create React App (irreversible)

## Browser Developer Tools

Install React Developer Tools browser extension:
- [Chrome Extension](https://chrome.google.com/webstore/detail/react-developer-tools/)
- [Firefox Extension](https://addons.mozilla.org/en-US/firefox/addon/react-devtools/)

This extension adds React-specific debugging tools to your browser's developer tools.

## Next Steps

With your environment set up, you're ready to start building React components! In the next lesson, we'll explore JSX and create your first custom component.`
        },
        {
          id: 'jsx-introduction',
          title: 'Introduction to JSX',
          type: 'video',
          duration: 25,
          description: 'Learn JSX syntax and how to write HTML-like code in JavaScript'
        },
        {
          id: 'first-component',
          title: 'Creating Your First Component',
          type: 'interactive',
          duration: 30,
          description: 'Hands-on practice creating React components',
          interactiveData: {
            instructions: 'Create a Welcome component that displays a personalized greeting',
            initialCode: `function Welcome({ name }) {
  return (
    <div>
      <h1>Welcome, {name}!</h1>
      <p>We're excited to have you learning React!</p>
    </div>
  );
}`,
            expectedOutput: 'Rendered output:\n<div>\n  <h1>Welcome, John!</h1>\n  <p>We\'re excited to have you learning React!</p>\n</div>',
            hints: [
              'Use curly braces {} to embed JavaScript expressions in JSX',
              'Props are passed as function parameters using destructuring',
              'JSX elements can contain both static text and dynamic values',
              'Multiple JSX elements need to be wrapped in a parent element'
            ]
          }
        }
      ],
      quiz: {
        id: 'react-fundamentals-quiz',
        title: 'React Fundamentals Quiz',
        description: 'Test your understanding of React basics',
        timeLimit: 10,
        passingScore: 70,
        attempts: 3,
        questions: [
          {
            id: 'q1',
            type: 'multiple-choice',
            question: 'What is JSX?',
            options: [
              'A new programming language',
              'A syntax extension for JavaScript',
              'A React library',
              'A build tool'
            ],
            correctAnswer: 1,
            explanation: 'JSX is a syntax extension for JavaScript that allows you to write HTML-like code in your JavaScript files.'
          },
          {
            id: 'q2',
            type: 'multiple-choice',
            question: 'Which command creates a new React application?',
            options: [
              'npm create react-app',
              'npx create-react-app',
              'react new app',
              'npm install react'
            ],
            correctAnswer: 1,
            explanation: 'npx create-react-app is the correct command to create a new React application.'
          },
          {
            id: 'q3',
            type: 'multiple-choice',
            question: 'What file is the entry point of a React application?',
            options: [
              'App.js',
              'index.html',
              'index.js',
              'main.js'
            ],
            correctAnswer: 2,
            explanation: 'index.js is typically the entry point where React is rendered into the DOM.'
          }
        ]
      },
      assignment: {
        id: 'react-portfolio-start',
        title: 'Personal Portfolio Setup',
        description: 'Create the foundation for a personal portfolio website using React',
        instructions: `Create a basic React application that will serve as your personal portfolio. This assignment will help you apply the fundamental concepts we've covered.

**Requirements:**
1. Create a new React app called 'my-portfolio'
2. Create the following components:
   - Header component with your name and navigation
   - About component with a brief introduction
   - Footer component with contact information
3. Use proper JSX syntax and component structure
4. Apply basic CSS styling to make it look presentable

**Submission Guidelines:**
- Zip your project folder (exclude node_modules)
- Include a README with setup instructions
- Deploy to GitHub Pages or Netlify (bonus points)

**Evaluation Criteria:**
- Correct React component structure (25 points)
- Proper JSX usage (25 points)
- Code organization and cleanliness (25 points)
- Creativity and design (25 points)`,
        type: 'project',
        dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 1 week from now
        submissionFormat: ['zip', 'url'],
        maxFileSize: 50,
        weight: 15,
        rubric: [
          { criteria: 'Component Structure', description: 'Proper React component creation and organization', maxPoints: 25 },
          { criteria: 'JSX Implementation', description: 'Correct JSX syntax and usage', maxPoints: 25 },
          { criteria: 'Code Quality', description: 'Clean, readable, and well-organized code', maxPoints: 25 },
          { criteria: 'Creativity', description: 'Creative design and additional features', maxPoints: 25 }
        ]
      }
    },
    {
      id: 'components-props',
      title: 'Components and Props',
      description: 'Deep dive into React components, props, and composition patterns',
      order: 2,
      estimatedHours: 1.2,
      content: [
        {
          id: 'component-types',
          title: 'Functional vs Class Components',
          type: 'video',
          duration: 20,
          description: 'Understanding different ways to create React components'
        },
        {
          id: 'props-deep-dive',
          title: 'Props Deep Dive',
          type: 'text',
          duration: 25,
          description: 'Master props, prop types, and default props',
          textContent: `# Props Deep Dive

## What are Props?

Props (short for properties) are the way data flows from parent to child components in React. They are read-only and help make components reusable and configurable.

## Passing Props

### Basic Props
\`\`\`jsx
// Parent Component
function App() {
  return (
    <UserCard 
      name="John Doe" 
      age={30} 
      email="john@example.com" 
    />
  );
}

// Child Component
function UserCard({ name, age, email }) {
  return (
    <div className="user-card">
      <h2>{name}</h2>
      <p>Age: {age}</p>
      <p>Email: {email}</p>
    </div>
  );
}
\`\`\`

### Object and Array Props
\`\`\`jsx
// Passing objects
const user = {
  name: "John Doe",
  age: 30,
  skills: ["JavaScript", "React", "Node.js"]
};

<UserProfile user={user} />

// Accessing object props
function UserProfile({ user }) {
  return (
    <div>
      <h2>{user.name}</h2>
      <ul>
        {user.skills.map(skill => (
          <li key={skill}>{skill}</li>
        ))}
      </ul>
    </div>
  );
}
\`\`\`

## Props Validation with PropTypes

PropTypes help catch bugs by validating the props passed to components:

\`\`\`jsx
import PropTypes from 'prop-types';

function UserCard({ name, age, email, isActive }) {
  return (
    <div className="user-card">
      <h2>{name}</h2>
      <p>Age: {age}</p>
      <p>Email: {email}</p>
      {isActive && <span className="active">Active</span>}
    </div>
  );
}

UserCard.propTypes = {
  name: PropTypes.string.isRequired,
  age: PropTypes.number.isRequired,
  email: PropTypes.string.isRequired,
  isActive: PropTypes.bool
};

UserCard.defaultProps = {
  isActive: false
};
\`\`\`

## Advanced Props Patterns

### Children Props
The special \`children\` prop allows components to wrap other content:

\`\`\`jsx
function Card({ title, children }) {
  return (
    <div className="card">
      <h3>{title}</h3>
      <div className="card-content">
        {children}
      </div>
    </div>
  );
}

// Usage
<Card title="My Card">
  <p>This content goes inside the card</p>
  <button>Click me</button>
</Card>
\`\`\`

### Render Props Pattern
\`\`\`jsx
function DataFetcher({ render }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData().then(result => {
      setData(result);
      setLoading(false);
    });
  }, []);

  return render({ data, loading });
}

// Usage
<DataFetcher 
  render={({ data, loading }) => (
    loading ? <div>Loading...</div> : <div>{data}</div>
  )}
/>
\`\`\`

## Component Composition

React encourages composition over inheritance. Build complex UIs by combining simple components:

\`\`\`jsx
function Header({ title, subtitle }) {
  return (
    <header>
      <h1>{title}</h1>
      {subtitle && <p>{subtitle}</p>}
    </header>
  );
}

function Navigation({ links }) {
  return (
    <nav>
      {links.map(link => (
        <a key={link.href} href={link.href}>
          {link.text}
        </a>
      ))}
    </nav>
  );
}

function Layout({ children }) {
  return (
    <div className="layout">
      <Header 
        title="My App" 
        subtitle="Welcome to React"
      />
      <Navigation 
        links={[
          { href: "/", text: "Home" },
          { href: "/about", text: "About" }
        ]}
      />
      <main>{children}</main>
    </div>
  );
}
\`\`\`

## Best Practices

1. **Keep props simple**: Pass only the data that components need
2. **Use destructuring**: Extract props for cleaner code
3. **Validate props**: Use PropTypes or TypeScript
4. **Provide defaults**: Use defaultProps for optional props
5. **Avoid prop drilling**: Consider Context API for deeply nested props

## Common Pitfalls

1. **Mutating props**: Props are read-only, never modify them
2. **Passing too many props**: Consider using objects or Context
3. **Missing keys**: Always provide keys for list items
4. **Inline object creation**: Can cause unnecessary re-renders

\`\`\`jsx
// ❌ Bad - creates new object on every render
<Component style={{ margin: 10 }} />

// ✅ Good - define outside or use useMemo
const style = { margin: 10 };
<Component style={style} />
\`\`\`

Understanding props thoroughly is crucial for building maintainable React applications. Practice these patterns and you'll be writing clean, reusable components in no time!`
        },
        {
          id: 'component-composition',
          title: 'Component Composition Exercise',
          type: 'interactive',
          duration: 35,
          description: 'Build a reusable card component with composition',
          interactiveData: {
            instructions: 'Create a flexible Card component that accepts title and children props',
            initialCode: `function Card({ title, children }) {
  return (
    <div className="card">
      {title && <h3>{title}</h3>}
      <div className="card-content">
        {children}
      </div>
    </div>
  );
}`,
            expectedOutput: 'Rendered output:\n<div className="card">\n  <h3>Value</h3>\n  <div className="card-content">\n    Value\n  </div>\n</div>',
            hints: [
              'Use conditional rendering for optional parts like title',
              'The children prop allows flexible content inside components',
              'Props are destructured from the function parameter',
              'JSX elements can contain both props and children',
              'Consider using template literals for combining class names'
            ]
          }
        }
      ],
      quiz: {
        id: 'components-props-quiz',
        title: 'Components and Props Mastery',
        description: 'Test your understanding of React components and props',
        timeLimit: 12,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: 'q1',
            type: 'multiple-choice',
            question: 'What is the correct way to pass a prop called "name" with value "React" to a component?',
            options: [
              '<Component name="React">',
              '<Component name={React}>',
              '<Component "name"="React">',
              '<Component {name: "React"}>'
            ],
            correctAnswer: 0,
            explanation: 'String props are passed using quotes: name="React"'
          },
          {
            id: 'q2',
            type: 'multiple-choice',
            question: 'How do you access the children prop in a functional component?',
            options: [
              'props.children',
              '{ children }',
              'this.props.children',
              'children'
            ],
            correctAnswer: 1,
            explanation: 'In functional components with destructuring: { children }'
          },
          {
            id: 'q3',
            type: 'multiple-choice',
            question: 'What is the purpose of PropTypes?',
            options: [
              'To style components',
              'To validate props during development',
              'To create new props',
              'To handle events'
            ],
            correctAnswer: 1,
            explanation: 'PropTypes validate the types of props passed to components during development'
          }
        ]
      }
    },
    {
      id: 'state-hooks',
      title: 'State and React Hooks',
      description: 'Master React state management with useState and useEffect hooks',
      order: 3,
      estimatedHours: 2.0,
      content: [
        {
          id: 'usestate-introduction',
          title: 'Introduction to useState Hook',
          type: 'video',
          duration: 30,
          description: 'Learn how to manage component state with the useState hook'
        },
        {
          id: 'useeffect-lifecycle',
          title: 'useEffect and Component Lifecycle',
          type: 'video',
          duration: 35,
          description: 'Understanding side effects and the component lifecycle'
        },
        {
          id: 'state-management-patterns',
          title: 'Advanced State Management Patterns',
          type: 'text',
          duration: 40,
          description: 'Learn complex state patterns and when to use them',
          textContent: `# Advanced State Management Patterns

## Complex State with useReducer

When state logic becomes complex, \`useReducer\` provides a more predictable way to manage state updates:

\`\`\`jsx
import React, { useReducer } from 'react';

// Define action types
const ACTIONS = {
  INCREMENT: 'increment',
  DECREMENT: 'decrement',
  RESET: 'reset',
  SET_VALUE: 'set_value'
};

// Reducer function
function counterReducer(state, action) {
  switch (action.type) {
    case ACTIONS.INCREMENT:
      return { count: state.count + 1 };
    case ACTIONS.DECREMENT:
      return { count: state.count - 1 };
    case ACTIONS.RESET:
      return { count: 0 };
    case ACTIONS.SET_VALUE:
      return { count: action.payload };
    default:
      throw new Error(\`Unknown action type: \${action.type}\`);
  }
}

function Counter() {
  const [state, dispatch] = useReducer(counterReducer, { count: 0 });

  return (
    <div>
      <p>Count: {state.count}</p>
      <button onClick={() => dispatch({ type: ACTIONS.INCREMENT })}>
        +
      </button>
      <button onClick={() => dispatch({ type: ACTIONS.DECREMENT })}>
        -
      </button>
      <button onClick={() => dispatch({ type: ACTIONS.RESET })}>
        Reset
      </button>
    </div>
  );
}
\`\`\`

## State Composition Patterns

### Lifting State Up
When multiple components need to share state, lift it to their common parent:

\`\`\`jsx
function TemperatureConverter() {
  const [celsius, setCelsius] = useState('');

  const fahrenheit = celsius === '' ? '' : (celsius * 9/5) + 32;

  return (
    <div>
      <TemperatureInput
        scale="c"
        temperature={celsius}
        onTemperatureChange={setCelsius}
      />
      <TemperatureInput
        scale="f"
        temperature={fahrenheit}
        onTemperatureChange={(f) => setCelsius((f - 32) * 5/9)}
      />
    </div>
  );
}

function TemperatureInput({ scale, temperature, onTemperatureChange }) {
  return (
    <fieldset>
      <legend>Enter temperature in {scale === 'c' ? 'Celsius' : 'Fahrenheit'}:</legend>
      <input
        value={temperature}
        onChange={(e) => onTemperatureChange(e.target.value)}
      />
    </fieldset>
  );
}
\`\`\`

### Custom Hooks for Reusable State Logic

Custom hooks allow you to extract and reuse stateful logic:

\`\`\`jsx
// Custom hook for local storage
function useLocalStorage(key, initialValue) {
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.error('Error reading localStorage key "' + key + '":', error);
      return initialValue;
    }
  });

  const setValue = (value) => {
    try {
      setStoredValue(value);
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error('Error setting localStorage key "' + key + '":', error);
    }
  };

  return [storedValue, setValue];
}

// Usage
function Settings() {
  const [theme, setTheme] = useLocalStorage('theme', 'light');
  const [language, setLanguage] = useLocalStorage('language', 'en');

  return (
    <div>
      <select value={theme} onChange={(e) => setTheme(e.target.value)}>
        <option value="light">Light</option>
        <option value="dark">Dark</option>
      </select>
      <select value={language} onChange={(e) => setLanguage(e.target.value)}>
        <option value="en">English</option>
        <option value="es">Spanish</option>
      </select>
    </div>
  );
}
\`\`\`

### State Machines with useReducer

For complex state transitions, consider using a state machine pattern:

\`\`\`jsx
const STATES = {
  IDLE: 'idle',
  LOADING: 'loading',
  SUCCESS: 'success',
  ERROR: 'error'
};

const ACTIONS = {
  FETCH_START: 'fetch_start',
  FETCH_SUCCESS: 'fetch_success',
  FETCH_ERROR: 'fetch_error',
  RESET: 'reset'
};

function dataFetchReducer(state, action) {
  switch (action.type) {
    case ACTIONS.FETCH_START:
      return { ...state, status: STATES.LOADING, error: null };
    case ACTIONS.FETCH_SUCCESS:
      return { 
        status: STATES.SUCCESS, 
        data: action.payload, 
        error: null 
      };
    case ACTIONS.FETCH_ERROR:
      return { 
        status: STATES.ERROR, 
        data: null, 
        error: action.payload 
      };
    case ACTIONS.RESET:
      return { status: STATES.IDLE, data: null, error: null };
    default:
      throw new Error(\`Unknown action: \${action.type}\`);
  }
}

function DataComponent() {
  const [state, dispatch] = useReducer(dataFetchReducer, {
    status: STATES.IDLE,
    data: null,
    error: null
  });

  const fetchData = async () => {
    dispatch({ type: ACTIONS.FETCH_START });
    try {
      const response = await fetch('/api/data');
      const data = await response.json();
      dispatch({ type: ACTIONS.FETCH_SUCCESS, payload: data });
    } catch (error) {
      dispatch({ type: ACTIONS.FETCH_ERROR, payload: error.message });
    }
  };

  return (
    <div>
      {state.status === STATES.IDLE && (
        <button onClick={fetchData}>Load Data</button>
      )}
      {state.status === STATES.LOADING && <div>Loading...</div>}
      {state.status === STATES.SUCCESS && (
        <div>Data: {JSON.stringify(state.data)}</div>
      )}
      {state.status === STATES.ERROR && (
        <div>Error: {state.error}</div>
      )}
    </div>
  );
}
\`\`\`

## Performance Optimization

### useMemo for Expensive Calculations
\`\`\`jsx
function ExpensiveComponent({ items, multiplier }) {
  const expensiveValue = useMemo(() => {
    console.log('Calculating expensive value...');
    return items.reduce((sum, item) => sum + item.value, 0) * multiplier;
  }, [items, multiplier]);

  return <div>Result: {expensiveValue}</div>;
}
\`\`\`

### useCallback for Stable Function References
\`\`\`jsx
function Parent() {
  const [count, setCount] = useState(0);
  const [other, setOther] = useState(0);

  const handleClick = useCallback(() => {
    setCount(c => c + 1);
  }, []); // No dependencies, function is stable

  return (
    <div>
      <Child onClick={handleClick} />
      <button onClick={() => setOther(other + 1)}>
        Other: {other}
      </button>
    </div>
  );
}

const Child = React.memo(({ onClick }) => {
  console.log('Child rendered');
  return <button onClick={onClick}>Click me</button>;
});
\`\`\`

## Best Practices

1. **Keep state minimal**: Only store what you need
2. **Prefer local state**: Lift state only when necessary
3. **Use the right hook**: useState for simple state, useReducer for complex logic
4. **Avoid state mutations**: Always create new objects/arrays
5. **Consider custom hooks**: Extract reusable logic
6. **Optimize wisely**: Profile before optimizing with useMemo/useCallback

These patterns will help you build scalable and maintainable React applications with proper state management!`
        },
        {
          id: 'hooks-practice',
          title: 'Hooks Practice: Todo App',
          type: 'interactive',
          duration: 45,
          description: 'Build a fully functional todo app using React hooks',
          interactiveData: {
            instructions: 'Practice using React hooks for state management with a simple counter',
            initialCode: `function Counter() {
  const [count, setCount] = useState(0);
  
  return (
    <div>
      <p>Count: {count}</p>
      <button onClick={() => setCount(count + 1)}>+</button>
      <button onClick={() => setCount(count - 1)}>-</button>
    </div>
  );
}`,
            expectedOutput: 'Rendered output:\n<div>\n  <p>Count: 0</p>\n  <button>+</button>\n  <button>-</button>\n</div>',
            hints: [
              'useState returns an array with the state value and setter function',
              'Use functional state updates when the new state depends on the previous state',
              'Event handlers can be inline arrow functions for simple operations',
              'The component will re-render when state changes',
              'useState accepts an initial value as its parameter'
            ]
          }
        }
      ],
      quiz: {
        id: 'state-hooks-quiz',
        title: 'State and Hooks Mastery',
        description: 'Test your understanding of React state and hooks',
        timeLimit: 15,
        passingScore: 80,
        attempts: 3,
        questions: [
          {
            id: 'q1',
            type: 'multiple-choice',
            question: 'When should you use useReducer instead of useState?',
            options: [
              'For simple boolean states',
              'For complex state logic with multiple sub-values',
              'Only in class components',
              'When you need to fetch data'
            ],
            correctAnswer: 1,
            explanation: 'useReducer is preferred for complex state logic that involves multiple sub-values or when the next state depends on the previous one in complex ways.'
          },
          {
            id: 'q2',
            type: 'multiple-choice',
            question: 'What does useEffect with an empty dependency array do?',
            options: [
              'Runs on every render',
              'Runs only once after the initial render',
              'Never runs',
              'Runs only when props change'
            ],
            correctAnswer: 1,
            explanation: 'useEffect with an empty dependency array [] runs only once after the initial render, similar to componentDidMount.'
          },
          {
            id: 'q3',
            type: 'multiple-choice',
            question: 'How do you update state that depends on the previous state value?',
            options: [
              'setState(state + 1)',
              'setState(prevState => prevState + 1)',
              'setState(this.state + 1)',
              'setState(1)'
            ],
            correctAnswer: 1,
            explanation: 'Use the functional update pattern: setState(prevState => prevState + 1) to ensure you\'re working with the latest state value.'
          }
        ]
      }
    },
    // Additional modules would continue here...
    // For brevity, I'll add a few more key modules
    {
      id: 'react-router',
      title: 'React Router and Navigation',
      description: 'Build single-page applications with client-side routing',
      order: 4,
      estimatedHours: 1.5,
      content: [
        {
          id: 'router-introduction',
          title: 'Introduction to React Router',
          type: 'video',
          duration: 25,
          description: 'Setting up React Router and basic navigation'
        },
        {
          id: 'dynamic-routing',
          title: 'Dynamic Routes and Parameters',
          type: 'video',
          duration: 30,
          description: 'Creating dynamic routes with URL parameters'
        },
        {
          id: 'navigation-project',
          title: 'Multi-Page App Project',
          type: 'interactive',
          duration: 35,
          description: 'Build a multi-page application with routing',
          interactiveData: {
            instructions: 'Create a simple navigation component with multiple links',
            initialCode: `function Navigation() {
  return (
    <nav>
      <a href="/">Home</a>
      <a href="/about">About</a>
      <a href="/contact">Contact</a>
    </nav>
  );
}`,
            expectedOutput: 'Rendered output:\n<nav>\n  <a href="/">Home</a>\n  <a href="/about">About</a>\n  <a href="/contact">Contact</a>\n</nav>',
            hints: [
              'Navigation components help users move between different pages',
              'Use semantic HTML elements like <nav> for accessibility',
              'Href attributes specify the destination URL',
              'Consider using React Router Link components for SPA navigation',
              'Navigation links are typically styled as a horizontal or vertical list'
            ]
          }
        }
      ],
      quiz: {
        id: 'router-quiz',
        title: 'React Router Quiz',
        description: 'Test your routing knowledge',
        timeLimit: 10,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: 'q1',
            type: 'multiple-choice',
            question: 'Which component is used to define a route in React Router?',
            options: ['Router', 'Route', 'Link', 'Navigate'],
            correctAnswer: 1,
            explanation: 'The Route component is used to define individual routes in React Router.'
          }
        ]
      }
    },
    {
      id: 'api-integration',
      title: 'API Integration and Data Fetching',
      description: 'Learn to fetch and manage data from APIs in React applications',
      order: 5,
      estimatedHours: 1.8,
      content: [
        {
          id: 'fetch-api',
          title: 'Fetching Data with useEffect',
          type: 'video',
          duration: 30,
          description: 'Learn to fetch data from APIs using fetch and useEffect'
        },
        {
          id: 'error-handling',
          title: 'Error Handling and Loading States',
          type: 'video',
          duration: 25,
          description: 'Implementing proper error handling and loading indicators'
        },
        {
          id: 'custom-fetch-hook',
          title: 'Custom Data Fetching Hook',
          type: 'interactive',
          duration: 35,
          description: 'Create a reusable custom hook for data fetching',
          interactiveData: {
            instructions: 'Create a UserProfile component that displays user information',
            initialCode: `function UserProfile({ user }) {
  return (
    <div className="profile">
      <h2>{user.name}</h2>
      <p>Email: {user.email}</p>
      <p>Role: {user.role}</p>
    </div>
  );
}`,
            expectedOutput: 'Rendered output:\n<div className="profile">\n  <h2>Value</h2>\n  <p>Email: Value</p>\n  <p>Role: Value</p>\n</div>',
            hints: [
              'Props are accessed using dot notation on the prop object',
              'Use semantic HTML elements for better structure',
              'Components can accept complex objects as props',
              'Template literals can be used for dynamic content',
              'Object properties are accessed with the dot notation'
            ]
          }
        }
      ]
    }
  ],
  finalExam: {
    id: 'react-mastery-final',
    title: 'React Mastery Final Exam',
    description: 'Comprehensive exam covering all React concepts',
    timeLimit: 45,
    passingScore: 85,
    attempts: 2,
    questions: [
      {
        id: 'final-q1',
        type: 'multiple_choice',
        question: 'What is the virtual DOM in React?',
        options: [
          'A real DOM element',
          'A JavaScript representation of the real DOM',
          'A browser API',
          'A React component'
        ],
        correctAnswer: 1,
        explanation: 'The virtual DOM is a JavaScript representation of the real DOM that React uses to optimize updates.'
      },
      {
        id: 'final-q2',
        type: 'multiple_choice',
        question: 'Which pattern is recommended for sharing state between components?',
        options: [
          'Global variables',
          'Local storage',
          'Lifting state up or Context API',
          'Inline styles'
        ],
        correctAnswer: 2,
        explanation: 'React recommends lifting state up to common ancestors or using Context API for sharing state.'
      }      
    ]
  }
};